import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Button, Input } from '@unified-checkout/design-system';
import type { IValidation } from 'libs/design-system/src/types/common/validations';
import type { ITagProps } from 'libs/design-system/src/types/tag/tag';

import type { IShareInputState, IShareModal, IValidationResult } from '@app/dashboard/types/innerLayout/reports/modals/share';

import { CustomerInfo } from './CustomerInfo';

import styles from './shareModalBody.module.scss';

export const ShareModalBody = ({ mutation, sharedList = [], localization }: IShareModal) => {
  const [tags, setTags] = useState<ITagProps[]>([]);
  const [inputState, setInputState] = useState<IShareInputState>({ status: 'default' });

  const emailValidation: IValidation = {
    pattern: /^(?!.*\.\.)[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
    error_msg: localization.errors.invalidEmail,
  };

  const { mutationFunction, mutationFunctionParams, mutationKey, onSuccessHandler } = mutation;
  
  const { mutate, isPending } = useMutation({
    mutationKey: mutationKey,
    mutationFn: (tags: ITagProps[]) => mutationFunction({ emails: tags.map(tag => tag.text), ...mutationFunctionParams }),
    onSuccess: (data, variables) => {
      setTags([]);
      resetInputState();
      onSuccessHandler?.(data, variables);
    },
    onError: (error) => {
      const emailsErrors = (error as any).response?.data?.errors?.emails;
      const allMessages = emailsErrors
        ? Object.values(emailsErrors).flat()
        : [];
      const lastErrorMessage =
        allMessages.length > 0
          ? allMessages[allMessages.length - 1]
          : localization.errors.shareFailed;

        setInputState({
        status: 'error',
        errorMessage: lastErrorMessage as string,
      });
    }
  });

  const isEmailAlreadyShared = (email: string) => {
    return sharedList.some(item => item.email.toLowerCase() === email.toLowerCase());
  };

  const isEmailInCurrentTags = (email: string) => {
    return tags.some(tag => tag.text.toLowerCase() === email.toLowerCase());
  };

  const resetInputState = () => {
    setInputState({ status: 'default' });
  };

  const validateEmail = (email: string): IValidationResult => {
    if (!emailValidation.pattern.test(email)) {
      return {
        isValid: false,
        status: 'error',
        message: localization.errors.invalidEmail
      };
    }

    if (isEmailInCurrentTags(email)) {
      return {
        isValid: false,
        status: 'error',
        message: localization.errors.emailAlreadyAdded
      };
    }

    if (isEmailAlreadyShared(email)) {
      return {
        isValid: false,
        status: 'warning',
        message: localization.errors.emailAlreadyShared
      };
    }

    return { isValid: true, status: 'default' };
  };

  const isValidEmail = (tag: ITagProps) => {
    const validation = validateEmail(tag.text);

    if (!validation.isValid) {
      setInputState({
        status: validation.status || 'error',
        errorMessage: validation.message
      });
    }
    return validation.isValid;
  };

  const handleTagAdd = (tag: ITagProps) => {
    setTags([...tags, tag]);
    resetInputState();
    return true;
  };

  const handleTagRemove = (tag: ITagProps) => {
    setTags(prevTags => prevTags.filter(t => t.id !== tag.id || t.text !== tag.text));
    resetInputState();
  };

  const handleShare = () => {
    if (tags.length === 0) return;
    mutate(tags);
  };

  return (
    <div className={styles['share-modal']}>
      <div className={styles['share-modal__input-section']}>
        <Input
          type="tags"
          id="share-emails"
          name="share-emails"
          placeholder={localization.inputPlaceholder}
          label={localization.inputLabel}
          tags={tags}
          onTagAdd={handleTagAdd}
          onTagRemove={handleTagRemove}
          tagValidator={isValidEmail}
          error={inputState.errorMessage}
          status={inputState.status}
        />
        <Button
          size="md"
          color="primary"
          variant="outlined"
          text={localization.shareButtonText}
          disabled={tags.length === 0 || isPending}
          loading={isPending}
          onClickHandler={handleShare}
        />
      </div>

      {sharedList.length > 0 && (
        <div className={styles['share-modal__shared-list']}>
          {sharedList.map((item, index) => (
            <CustomerInfo
              key={`${item.email}-${index}`}
              email={item.email}
              status={item.status}
            />
          ))}
        </div>
      )}
    </div>
  );
}; 